/** Scoped requests change with answer selection; the validated page visit stays stable. */
import type { ShopifyResultQuestionSelection } from '@testfeed/shopify-contracts'
import { shopifyQueryPolicies } from '../queries/shopifyQueryPolicies'
import { useQuery } from '@tanstack/react-query'
import { usePageLifecycle } from './usePageLifecycle'
import { page } from '../pages/direct-study-results.page'
import { useShopifyAppContext } from '../ShopifyAppContext'
import type { ShopifyAppBridge } from '../shopifyApiFetch'
import { readDirectBuilderSource } from './study-builder/studyAuthoringBootstrap'
import { readStudyAuthoringResults } from './study-builder/studyAuthoringClient'
import { trackShopifyRefetch } from '../shopifyUiTracking'

export function useDirectStudyResults(
  shopify: ShopifyAppBridge,
  studyId: string,
  runId: string | null,
  questionSelection?: ShopifyResultQuestionSelection,
) {
  const scope = useShopifyAppContext()
  const selectionKey = JSON.stringify([
    questionSelection?.phrasesPage,
    questionSelection?.nextTestsPage,
    questionSelection?.questionId,
    questionSelection?.answerArmId,
    questionSelection?.answerPage,
  ])
  const visit = `${scope.shopDomain}:${scope.orgId}:${scope.connectionId}:${studyId}:${runId}`
  const read = useQuery({
    ...shopifyQueryPolicies.studyAuthoring,
    queryKey: [
      'direct-study-results',
      scope.shopDomain,
      scope.orgId,
      scope.connectionId,
      studyId,
      runId,
      questionSelection,
    ],
    retry: false,
    queryFn: async ({ signal }) => {
      const source = await readDirectBuilderSource(shopify, { studyId }, signal)
      if (source.deletion) return { selectionKey, results: null, authoring: null, deletion: source.deletion }
      // Name the exact source keys this page validates. `readDirectBuilderSource` also returns the
      // builder-only `startingDraft`; spreading it here would leak an extra key that the page's strict
      // `source` union rejects (unrecognized_keys → invalid-data).
      return {
        selectionKey,
        authoring: source.authoring,
        deletion: source.deletion,
        results: await readStudyAuthoringResults(
          shopify,
          { studyId, ...(runId ? { runId } : {}), ...questionSelection },
          signal,
        ),
      }
    },
    refetchInterval: (query) => {
      const source = query.state.data
      if (source?.deletion?.status === 'accepted' || source?.deletion?.status === 'cleaning') return 5000
      return source?.results?.view.refreshAfterMs ?? false
    },
  })
  const lifecycle = usePageLifecycle(
    page,
    visit,
    shopify,
    { result: { ...read, identity: selectionKey } },
    { setupId: studyId },
  )
  const source = lifecycle.data.applied.result
  const current = source?.selectionKey === selectionKey
  const lifecycleError =
    lifecycle.state.status === 'error' || (lifecycle.state.status === 'ready' && lifecycle.state.refreshFailed !== null)
  const retry = () => {
    lifecycle.retryFailedLoad()
    void trackShopifyRefetch(shopify, () => read.refetch())
  }
  return { read, lifecycle, source, visit, selectionKey, current, failed: read.isError || lifecycleError, retry }
}
